import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useFetchActivityLogs } from '../services/mutations';

// Helper function to generate initials from full name
const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const ActivityLog = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [logs, setLogs] = useState([]);
  const [totalPages, setTotalPages] = useState(0);

  // Filter states - separate pending filters from applied filters
  const [pendingFilters, setPendingFilters] = useState({
    method: '',
    url: '',
    resp_status: '',
    user_id: '',
  });

  const [appliedFilters, setAppliedFilters] = useState({
    method: '',
    url: '',
    resp_status: '',
    user_id: '',
  });

  const fetchActivityLogs = useFetchActivityLogs();

  // Single useEffect to fetch data when page, limit, or applied filters change
  useEffect(() => {
    const payload = { limit, page };

    // Add filters if they have values
    if (appliedFilters.method) payload.method = appliedFilters.method;
    if (appliedFilters.url) payload.url = appliedFilters.url;
    if (appliedFilters.resp_status)
      payload.resp_status = parseInt(appliedFilters.resp_status);
    if (appliedFilters.user_id) payload.user_id = appliedFilters.user_id;

    fetchActivityLogs.mutate(payload, {
      onSuccess: (response) => {
        setLogs(response?.data || []);
        setTotalPages(response?.meta?.total_pages || 0);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    limit,
    appliedFilters.method,
    appliedFilters.url,
    appliedFilters.resp_status,
    appliedFilters.user_id,
  ]);

  const handlePageChange = (value) => {
    setPage(value);
  };

  const handleLimitChange = (value) => {
    setLimit(value);
    setPage(1); // Reset to first page when changing page size
  };

  const handleFilterChange = (field, value) => {
    setPendingFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(pendingFilters); // Apply the pending filters
    setPage(1); // Reset to first page when applying filters
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      method: '',
      url: '',
      resp_status: '',
      user_id: '',
    };
    setPendingFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(1);
  };

  // Generate pagination items
  const getPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
        </PaginationItem>,
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis-start">
            <span className="flex h-9 w-9 items-center justify-center">
              ...
            </span>
          </PaginationItem>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => handlePageChange(i)}
            isActive={i === page}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis-end">
            <span className="flex h-9 w-9 items-center justify-center">
              ...
            </span>
          </PaginationItem>,
        );
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    return items;
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="text-2xl font-bold">Activity Log</h1>
          <p className="muted-text">Pantau request API dan status respon</p>
        </div>
      </div>

      <div className="section-card p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
          <div>
            <Label htmlFor="method" className="mb-2 block text-sm">
              Method
            </Label>
            <Select
              value={pendingFilters.method || 'all'}
              onValueChange={(value) =>
                handleFilterChange('method', value === 'all' ? '' : value)
              }
            >
              <SelectTrigger className="h-9 sm:h-10">
                <SelectValue placeholder="All Methods" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="GET">GET</SelectItem>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="PUT">PUT</SelectItem>
                <SelectItem value="PATCH">PATCH</SelectItem>
                <SelectItem value="DELETE">DELETE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="url" className="mb-2 block text-sm">
              URL
            </Label>
            <Input
              id="url"
              value={pendingFilters.url}
              onChange={(e) => handleFilterChange('url', e.target.value)}
              placeholder="e.g., /api/v1/user"
              className="h-9 sm:h-10"
            />
          </div>
          <div>
            <Label htmlFor="resp_status" className="mb-2 block text-sm">
              Response Status
            </Label>
            <Input
              id="resp_status"
              type="number"
              value={pendingFilters.resp_status}
              onChange={(e) =>
                handleFilterChange('resp_status', e.target.value)
              }
              placeholder="e.g., 200, 404"
              className="h-9 sm:h-10"
            />
          </div>
          <div>
            <Label htmlFor="user_id" className="mb-2 block text-sm">
              User ID
            </Label>
            <Input
              id="user_id"
              value={pendingFilters.user_id}
              onChange={(e) => handleFilterChange('user_id', e.target.value)}
              placeholder="Enter user ID"
              className="h-9 sm:h-10"
            />
          </div>
        </div>
        <div className="flex gap-2 sm:gap-3">
          <Button onClick={handleApplyFilters} className="flex-1 sm:flex-none h-9 sm:h-10 text-sm">
            Apply
          </Button>
          <Button
            variant="outline"
            onClick={handleClearFilters}
            className="flex-1 sm:flex-none h-9 sm:h-10 text-sm"
          >
            Clear
          </Button>
        </div>

        {fetchActivityLogs.isError && (
          <Alert variant="destructive" className="mt-3">
            <AlertDescription>
              {fetchActivityLogs.error?.response?.data?.detail ||
                fetchActivityLogs.error?.message ||
                'Failed to fetch activity logs'}
            </AlertDescription>
          </Alert>
        )}

        {fetchActivityLogs.isLoading ? (
          <div className="flex justify-center items-center mt-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            <div className="table-card mt-4">
              <div className="table-wrapper">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px] font-bold border-b-2 border-gray-200">
                        No
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Method
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        URL
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Status
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Timestamp
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        User
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Role
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-3">
                        No activity logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log, index) => (
                      <TableRow key={log.id}>
                        <TableCell
                          width="70"
                          className="border-b-2 border-gray-200"
                        >
                          {(page - 1) * limit + index + 1}
                        </TableCell>
                        <TableCell className="border-b-2 border-gray-200">
                          {log.method}
                        </TableCell>
                        <TableCell className="border-b-2 border-gray-200">
                          {log.url}
                        </TableCell>
                        <TableCell
                          className={`border-b-2 border-gray-200 ${
                            log.resp_status === 200
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          {log.resp_status}
                        </TableCell>
                        <TableCell className="border-b-2 border-gray-200">
                          {new Date(log.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="border-b-2 border-gray-200">
                          <div className="flex items-center gap-2">
                            <Avatar className="bg-primary text-primary-foreground">
                              <AvatarFallback>
                                {getInitials(log.full_name)}
                              </AvatarFallback>
                              {log.picture_url && (
                                <AvatarImage
                                  src={log.picture_url}
                                  alt={log.full_name}
                                />
                              )}
                            </Avatar>
                            <span>{log.full_name || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell
                          width="100"
                          className="border-b-2 border-gray-200"
                        >
                          {log.user_roles && log.user_roles.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {log.user_roles.map((role, idx) => (
                                <Badge
                                  key={idx}
                                  variant="secondary"
                                  className="text-xs text-black"
                                >
                                  {role}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              -
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 sm:mt-6 gap-3 sm:gap-4">
              <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start">
                <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                  Jumlah per halaman:
                </span>
                <Select
                  value={limit.toString()}
                  onValueChange={(value) => handleLimitChange(Number(value))}
                >
                  <SelectTrigger className="w-[70px] sm:w-[80px] h-8 sm:h-10 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {totalPages > 1 && (
                <Pagination className="w-full sm:w-auto">
                  <PaginationContent className="gap-1">
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => handlePageChange(Math.max(1, page - 1))}
                        className={`h-8 sm:h-10 text-xs sm:text-sm ${
                          page === 1
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }`}
                      />
                    </PaginationItem>
                    {getPaginationItems()}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() =>
                          handlePageChange(Math.min(totalPages, page + 1))
                        }
                        className={`h-8 sm:h-10 text-xs sm:text-sm ${
                          page === totalPages
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }`}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
